/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpHoldSignDialog.tsx
 * FILE_ID: JOBHUB-QA-ITP-HOLD-SIGN-001
 * PURPOSE: Sign one hold-point step from the ITP preview.
 */
import { Suspense, lazy, useState } from 'react'
import type { QaApi } from '../../../qa/qaApi'

const QaItpSignaturePad = lazy(() => import('./QaItpSignaturePad'))

export function QaItpHoldSignDialog({
  itemId,
  areaId,
  jobId,
  api,
  onClose,
}: {
  itemId: string
  areaId: string
  jobId: number
  api: Pick<QaApi, 'requestHoldPoint' | 'approveHoldPoint' | 'uploadFile'>
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [pad, setPad] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const save = async (png: Uint8Array) => {
    setPad(false)
    setBusy(true)
    setError(null)
    try {
      // Reused from: hold-points/useQaHoldPointSignoff.ts (onPadSaved)
      const hold = await api.requestHoldPoint({ areaId, itemInstanceId: itemId, name: 'Hold point' })
      const file = new File([new Uint8Array(png)], `signature-${itemId}.png`, { type: 'image/png' })
      const uploaded = await api.uploadFile(file, jobId)
      await api.approveHoldPoint(hold.id, {
        signatureAssetId: uploaded.id,
        signerName: name.trim(),
        signerRole: role.trim(),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the signature.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-label="Sign hold point" className="w-full max-w-sm rounded-xl border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Sign hold point</h2>
        <div className="mt-3 flex flex-col gap-2">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name" aria-label="Signer name" className="min-h-9 rounded-md border border-input px-2 text-sm" />
          <input value={role} onChange={(event) => setRole(event.target.value)} placeholder="Role" aria-label="Signer role" className="min-h-9 rounded-md border border-input px-2 text-sm" />
          {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border px-2 py-1 text-xs">Cancel</button>
            <button type="button" disabled={busy || !name.trim()} onClick={() => setPad(true)} className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">Sign</button>
          </div>
        </div>
      </div>
      {pad ? (
        <Suspense fallback={null}>
          <QaItpSignaturePad onCancel={() => setPad(false)} onSaved={(png) => { void save(png) }} />
        </Suspense>
      ) : null}
    </div>
  )
}
